/* oxlint-disable jsx-a11y/no-noninteractive-tabindex -- focusable overflow lets keyboard users scroll the activity log */
import { useQuery } from '@tanstack/react-query'
import type { CallsignScheduleNote, CallsignScheduleNoteAction } from '@transreport/pauk-rrms-api'
import { getCallsignScheduleNotes } from '@transreport/pauk-rrms-api'
import { format, isSameDay, isSameMonth, isSameYear, parseISO, subDays } from 'date-fns'
import { Pencil, Plus, Trash2, type LucideIcon } from 'lucide-react'
import type React from 'react'
import { useTranslation } from 'react-i18next'

import { SkeletonBlock } from '@/components/framework'
import { useOperatingStations } from '@/hooks/useOperatingStations'

type ActivityGroup = {
  date: string
  notes: CallsignScheduleNote[]
}

type ActionAppearance = {
  Icon: LucideIcon
  iconClassName: string
}

const ACTION_APPEARANCE: Record<CallsignScheduleNoteAction, ActionAppearance> = {
  create: {
    Icon: Plus,
    iconClassName: 'border-green-200 bg-green-50 text-green-700',
  },
  update: {
    Icon: Pencil,
    iconClassName: 'border-brand bg-brand-surface text-brand-ink',
  },
  delete: {
    Icon: Trash2,
    iconClassName: 'border-red-200 bg-red-50 text-red-700',
  },
}

const ActivityLogSkeleton = ({ label }: { label: string }) => (
  <div role="status" aria-label={label} className="space-y-8">
    <span className="sr-only">{label}</span>
    {[0, 1].map((group) => (
      <div key={group} aria-hidden="true">
        <SkeletonBlock className="mb-3 h-4 w-48" />
        <div className="space-y-4 pl-11">
          {[0, 1].map((item) => (
            <div key={item} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <SkeletonBlock className="h-5 w-48 max-w-full" />
              <SkeletonBlock className="mt-2 h-4 w-32" />
              <div className="mt-4 flex gap-2">
                <SkeletonBlock className="h-6 w-20" />
                <SkeletonBlock className="h-6 w-24" />
              </div>
              <SkeletonBlock className="mt-5 h-4 w-full" />
              <SkeletonBlock className="mt-3 h-4 w-3/4" />
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
)

function groupNotesByDate(notes: CallsignScheduleNote[]): ActivityGroup[] {
  const groups = new Map<string, CallsignScheduleNote[]>()

  notes.forEach((note) => {
    const date = format(parseISO(note.created_at), 'yyyy-MM-dd')
    const existingNotes = groups.get(date) ?? []
    groups.set(date, [...existingNotes, note])
  })

  return Array.from(groups, ([date, groupedNotes]) => ({ date, notes: groupedNotes }))
}

function formatActivePeriod(note: CallsignScheduleNote): string | null {
  const activeFrom = parseISO(note.active_from)
  if (!note.active_until) return null

  const activeUntil = subDays(parseISO(note.active_until), 1)
  if (isSameDay(activeFrom, activeUntil)) return format(activeFrom, 'd MMM yyyy')

  if (isSameMonth(activeFrom, activeUntil)) {
    return `${format(activeFrom, 'd')}–${format(activeUntil, 'd MMM yyyy')}`
  }

  if (isSameYear(activeFrom, activeUntil)) {
    return `${format(activeFrom, 'd MMM')}–${format(activeUntil, 'd MMM yyyy')}`
  }

  return `${format(activeFrom, 'd MMM yyyy')}–${format(activeUntil, 'd MMM yyyy')}`
}

const ActivityOperation: React.FC<{ note: CallsignScheduleNote }> = ({ note }) => {
  const { t } = useTranslation()
  const { Icon, iconClassName } = ACTION_APPEARANCE[note.action]
  const scheduleType = t(`resources.schedule.types.${note.schedule_type}`)
  const title = t(`resources.activityLog.actions.${note.action}`, { scheduleType })
  const titleId = `activity-operation-${note.id}`
  const activePeriod =
    formatActivePeriod(note) ??
    t('resources.activityLog.effectiveFrom', {
      date: format(parseISO(note.active_from), 'd MMM yyyy'),
    })

  return (
    <li className="relative pl-11">
      <span
        className={`absolute top-5 left-0 z-10 flex size-8 items-center justify-center rounded-full border ${iconClassName}`}
      >
        <Icon aria-hidden="true" className="size-4" strokeWidth={2} />
      </span>

      <article
        aria-labelledby={titleId}
        className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
      >
        <div className="border-b border-gray-100 px-5 py-4">
          <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
            <div>
              <h3 id={titleId} className="font-semibold text-gray-950">
                {title}
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                {t('resources.activityLog.by', { name: note.created_by_name })}
              </p>
            </div>
            <time
              className="text-sm font-medium whitespace-nowrap text-gray-600"
              dateTime={note.created_at}
            >
              {format(parseISO(note.created_at), 'HH:mm')}
            </time>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
              {scheduleType}
            </span>
            <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
              {t(`resources.schedule.applicability.${note.applicability}`)}
            </span>
            <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
              {activePeriod}
            </span>
          </div>
        </div>

        <div className="px-5 py-4">
          <p className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
            {t('resources.activityLog.changeCount', { count: note.entries.length })}
          </p>
          <ul
            className="mt-2 divide-y divide-gray-100"
            aria-label={t('resources.activityLog.changes')}
          >
            {note.entries.map((entry, index) => (
              <li key={`${note.id}-${index}`} className="flex gap-3 py-2.5 text-sm text-gray-800">
                <span
                  aria-hidden="true"
                  className="mt-1.5 size-1.5 shrink-0 rounded-full bg-gray-400"
                />
                <span>{entry}</span>
              </li>
            ))}
          </ul>
        </div>
      </article>
    </li>
  )
}

export const ResourcesActivityLogPage: React.FC = () => {
  const { t } = useTranslation()
  const { selectedStationCode } = useOperatingStations()

  const {
    data: notes,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['callsignScheduleNotes', selectedStationCode],
    queryFn: async () => {
      const stationCode = selectedStationCode ?? ''
      const [baselineNotes, overrideNotes] = await Promise.all([
        getCallsignScheduleNotes({
          schedule_type: 'baseline',
          station_code: stationCode,
        }),
        getCallsignScheduleNotes({
          schedule_type: 'override',
          station_code: stationCode,
        }),
      ])

      return [...baselineNotes, ...overrideNotes].toSorted((left, right) =>
        right.created_at.localeCompare(left.created_at)
      )
    },
    enabled: Boolean(selectedStationCode),
  })

  const activityGroups = notes ? groupNotesByDate(notes) : []

  return (
    <div className="flex size-full flex-col bg-gray-50">
      <h1 id="activity-log-heading" className="sr-only">
        {t('resources.activityLog.title')}
      </h1>

      <div
        role="region"
        aria-labelledby="activity-log-heading"
        tabIndex={0}
        className="flex-1 overflow-auto px-4 py-6 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-focus focus-visible:ring-inset sm:px-6"
      >
        <div className="mx-auto max-w-4xl">
          {!selectedStationCode && (
            <p className="text-sm text-gray-700">{t('common.selectStationFirst')}</p>
          )}

          {selectedStationCode && isLoading && <ActivityLogSkeleton label={t('common.loading')} />}

          {selectedStationCode && error && (
            <p role="alert" className="text-sm text-danger">
              {t('shiftTimeline.errorLoading')}
            </p>
          )}

          {selectedStationCode && !isLoading && !error && activityGroups.length === 0 && (
            <div className="rounded-xl border border-gray-200 bg-white px-6 py-10 text-center shadow-sm">
              <p className="text-sm text-gray-600">{t('resources.activityLog.empty')}</p>
            </div>
          )}

          {selectedStationCode && !isLoading && !error && activityGroups.length > 0 && (
            <div className="space-y-8">
              {activityGroups.map((group) => {
                const headingId = `activity-date-${group.date}`

                return (
                  <section key={group.date} aria-labelledby={headingId}>
                    <h2 id={headingId} className="mb-3 text-sm font-semibold text-gray-700">
                      {format(parseISO(group.date), 'EEEE, d MMMM yyyy')}
                    </h2>
                    <ol className="relative space-y-4 before:absolute before:inset-y-5 before:left-[15px] before:w-px before:bg-gray-200">
                      {group.notes.map((note) => (
                        <ActivityOperation key={note.id} note={note} />
                      ))}
                    </ol>
                  </section>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
